import axios from 'axios';
import scanApi from './api';

const BASE_URL = 'https://scanner.exploitfinder.com:8001';

// Creiamo l'istanza axios per il subdomain scanner
const axiosInstance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Aggiungiamo l'interceptor per il token di autenticazione
axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Interceptor per gestire gli errori di autenticazione
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            localStorage.removeItem('token');
            window.dispatchEvent(new Event('authError'));
        }
        return Promise.reject(error);
    }
);

export const subdomainApi = {
    startSubdomainScan: async (domain, enableNmap) => {
        try {
            // Verifica autenticazione
            const authStatus = await scanApi.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            // Verifica quota
            const userProfile = await scanApi.getCurrentUser();
            console.log('User Profile:', userProfile); // Debug log

            if (!userProfile.quotaInfo || userProfile.quotaInfo.remaining <= 0) {
                throw new Error('QUOTA_EXCEEDED');
            }

            // Procedi con la scansione
            const response = await axiosInstance.post('/scan', {
                domain,
                enable_nmap: enableNmap,
                userId: userProfile.id,
                userQuota: userProfile.quotaInfo
            });

            // Aggiorna la quota dopo la scansione riuscita
            await scanApi.refreshQuota();

            // Notifica il frontend dell'aggiornamento della quota
            if (response.data.quotaInfo) {
                scanApi.notifyQuotaUpdate(response.data.quotaInfo);
            }

            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
            }
            if (error.message === 'QUOTA_EXCEEDED') {
                window.dispatchEvent(new CustomEvent('quotaExceeded', {
                    detail: {
                        message: 'Quota scansioni esaurita. Acquista altre scansioni per continuare.'
                    }
                }));
            }
            throw error;
        }
    },

    getSubdomainScanStatus: async (scanId) => {
        try {
            const authStatus = await scanApi.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            const userProfile = await scanApi.getCurrentUser();
            const response = await axiosInstance.get(`/scan/${scanId}/status`, {
                params: {
                    userId: userProfile.id
                }
            });
            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
            }
            throw error;
        }
    },

    getLiveResults: async (scanId) => {
        try {
            const authStatus = await scanApi.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            const userProfile = await scanApi.getCurrentUser();
            const response = await axiosInstance.get(`/scan/${scanId}/live`, {
                params: {
                    userId: userProfile.id
                }
            });
            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
            }
            throw error;
        }
    },

    getScanHistory: async (scanId) => {
        try {
            const authStatus = await scanApi.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            const userProfile = await scanApi.getCurrentUser();
            const response = await axiosInstance.get(`/scan/${scanId}/resume`, {
                params: {
                    userId: userProfile.id
                }
            });
            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
            }
            throw error;
        }
    },

    getNmapReport: async (scanId) => {
        try {
            const authStatus = await scanApi.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            const userProfile = await scanApi.getCurrentUser();
            const response = await axiosInstance.get(`/scan/${scanId}/nmap`, {
                params: {
                    userId: userProfile.id
                }
            });
            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
            }
            throw error;
        }
    },

    stopScan: async (scanId) => {
        try {
            const authStatus = await scanApi.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            const userProfile = await scanApi.getCurrentUser();
            const response = await axiosInstance.post(`/scan/${scanId}/stop`, {
                userId: userProfile.id
            });
            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
            }
            throw error;
        }
    },

    getAllScans: async () => {
        try {
            const authStatus = await scanApi.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            const userProfile = await scanApi.getCurrentUser();
            const response = await axiosInstance.get('/scans', {
                params: {
                    userId: userProfile.id
                }
            });
            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
            }
            throw error;
        }
    },

    getScanSummary: async (scanId) => {
        const response = await axios.get(`${BASE_URL}/scan/${scanId}/summary`);
        return response.data;
    },

    exportScan: async (scanId) => {
        const response = await axios.get(`${BASE_URL}/scan/${scanId}/export`);
        return response.data;
    },

    getNmapSummary: async (scanId) => {
        const response = await axios.get(`${BASE_URL}/scan/${scanId}/nmap`);
        return response.data;
    },

    getNmapDomainDetails: async (scanId, domain) => {
        const response = await axios.get(`${BASE_URL}/scan/${scanId}/nmap/${domain}`);
        return response.data;
    }
}; 